import { useConfig } from '@bluheadless/ui-logic/src/providers/config'
import { mediaUrl } from '@bluheadless/ui-logic/src/assets'
import { useStoreLocatorUrl } from '@bluheadless/ui-logic/src/hooks/store-locator/useStoreLocatorUrl'
import { cx } from '@emotion/css'
import { object, string, shape } from 'prop-types'
import { useState } from 'react'
import { distanceInKm } from '../../../utils/store-locator'
import { Root, GetDirection } from './store-locator-card.styled'
import { Grid } from '@mui/material'
import Typography from '../../../atoms/typography'
import WorkingTimesTable from '../list/working-times/working-times-table'
import { AddressWrapper } from '../list/store-locator-list.styled'
import Card from '../../card'
import Image from '../../../particles/image'
import BHTooltip from '../../tooltip'
import { FormattedMessage, useIntl } from 'react-intl'
import Button from '../../button'
import MapPinIcon from '../../../atoms/icons/map-pin-icon'
import { useTheme } from '@mui/system'
import useMediaQuery from '@mui/material/useMediaQuery'
import Link from '../../link'

const StoreLocatorCard = ({ store, className, workingTimeTooltipProps, ...props }) => {
	const { formatMessage } = useIntl()
	const theme = useTheme()
	const smUp = useMediaQuery(theme.breakpoints.up('sm'))

	const {
		siteName,
		storelocator: { details },
	} = useConfig()

	const {
		id,
		title,
		street,
		city,
		postcode,
		countryCode,
		phone1,
		phone2,
		distanceInMt,
		workingTimes,
		image,
		latitude,
		longitude,
		email,
		websiteUrl,
		isEnabledShopDetails,
		urlKey: _urlKey,
	} = store

	const urlKey = useStoreLocatorUrl(id, _urlKey)

	const [workingTimesTableOpen, setWorkingTimesTableOpen] = useState(false)

	return (
		<Root className={cx('StoreLocatorCard-root', className)} {...props}>
			<Card
				elevation={0}
				variant="outlined"
				header={
					image ? (
						<Link href={details && !!isEnabledShopDetails ? urlKey : null} underline="none">
							<Image src={mediaUrl(image)} alt={title + ' | ' + siteName} />
						</Link>
					) : null
				}
				content={
					<Grid container justifyContent="space-between" spacing={0} sx={{ position: 'relative' }}>
						<Grid item xs={12}>
							{smUp && <MapPinIcon className="icon-pin" />}
							<Link href={details && !!isEnabledShopDetails ? urlKey : null} underline="none">
								<Typography component="h2" variant="h6">
									{title}
								</Typography>
							</Link>
							{distanceInMt && (
								<Typography component="p" variant="caption">{`${distanceInKm(distanceInMt)} km`}</Typography>
							)}
						</Grid>
						<Grid item xs={12}>
							<AddressWrapper container justifyContent="space-between" wrap="nowrap" spacing={0}>
								<Grid item xs={distanceInMt ? 10 : 'auto'}>
									{street && <Typography color="grey.main">{street}</Typography>}
									<Typography color="grey.main">
										{city && <Typography component="span">{city}</Typography>}
										{postcode && <Typography component="span">{', ' + postcode}</Typography>}
										{countryCode && <Typography component="span">{' - ' + countryCode}</Typography>}
									</Typography>
								</Grid>
							</AddressWrapper>
							<AddressWrapper container justifyContent="space-between" wrap="nowrap" spacing={0}>
								<Grid item xs={'auto'}>
									{phone1 && (
										<Typography color="grey.main">
											{formatMessage({ id: 'store_locator_telephone' }) + ': ' + phone1}
										</Typography>
									)}
									{email && (
										<Typography color="grey.main">
											{formatMessage({ id: 'store_locator_email' }) + ': ' + email}
										</Typography>
									)}
									{phone2 && (
										<>
											<Typography color="grey.main">
												{formatMessage({ id: 'store_locator_whatsapp' }) + ': ' + phone2}
											</Typography>
											<Button variant="text" target="_blank" href={`https://wa.me/${phone2}`}>
												{formatMessage({ id: 'store_locator_start_chat' })}
											</Button>
										</>
									)}
									{websiteUrl && (
										<Typography color="grey.main">
											{formatMessage({ id: 'store_locator_website' }) + ': ' + websiteUrl}
										</Typography>
									)}
								</Grid>
							</AddressWrapper>
							{workingTimes && (
								<Grid item xs={12}>
									<WorkingTimesTable
										workingTimes={workingTimes}
										open={workingTimesTableOpen}
										setOpen={setWorkingTimesTableOpen}
										workingTimeTooltipProps={workingTimeTooltipProps}
									/>
								</Grid>
							)}
							<GetDirection
								href={`https://www.google.com/maps/place/${latitude},${longitude}`}
								target="_blank"
								variant="text"
							>
								<FormattedMessage id="store_locator_get_direction" />
							</GetDirection>
						</Grid>
					</Grid>
				}
			/>
		</Root>
	)
}

StoreLocatorCard.defaultProps = {}

StoreLocatorCard.propTypes = {
	store: object,
	apiConfigs: object,
	className: string,
	workingTimeTooltipProps: shape({ ...BHTooltip.propTypes }),
}

export default StoreLocatorCard
